import { Link } from 'react-router-dom';
import {
  FireIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
  UserGroupIcon,
  LightBulbIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';

export default function FlowHeatAnalysisPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/help" className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Flow Heat Analysis</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
            <FireIcon className="h-8 w-8 text-orange-500 mr-3" />
            Flow Heat Analysis
          </h1>
          <p className="text-lg text-gray-600">
            Identify your most critical flows based on real usage data and error rates
          </p>
        </div>

        {/* What is Heat Analysis */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-blue-500 mr-2" />
            What is Flow Heat Analysis?
          </h2>
          <p className="text-gray-700 mb-4">
            Flow Heat Analysis examines how your flows are actually being used in production. 
            It analyzes execution patterns, error rates, and business impact to calculate a 
            "heat score" that helps you prioritize which flows need attention.
          </p>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Key Insight:</strong> A flow with high usage and high error rates will have 
              a much higher heat score than a rarely-used flow with occasional errors, helping you 
              focus on fixes that impact the most users.
            </p>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            How It Works
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  1
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Click "Calculate Heat Scores"</h3>
                <p className="text-gray-600">
                  Navigate to the Flows page and select the "Usage Heat" tab. Click the 
                  "Calculate Heat Scores" button to start the analysis.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  2
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">FlowDocs Queries Salesforce</h3>
                <p className="text-gray-600">
                  We analyze your flow execution history from the last 30 days, including:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600 list-disc list-inside">
                  <li>Total number of executions per flow</li>
                  <li>Unique users who ran each flow</li>
                  <li>Error counts and failure rates</li>
                  <li>Most recent execution times</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  3
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Calculate Heat Scores</h3>
                <p className="text-gray-600">
                  Each flow receives a heat score (0-100) based on usage frequency, error rates, 
                  performance issues, and business impact.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  4
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">View Prioritized Results</h3>
                <p className="text-gray-600">
                  Flows are displayed with heat indicators, sorted by priority. Focus on 
                  "Hot" and "On Fire" flows first for maximum impact.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding Heat Levels */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Understanding Heat Levels
          </h2>
          
          <div className="space-y-3">
            <div className="flex items-center p-3 rounded-lg bg-red-50">
              <span className="text-2xl mr-3">🔥</span>
              <div>
                <h3 className="font-medium text-red-900">On Fire (80-100)</h3>
                <p className="text-sm text-red-700">
                  Critical flows with high usage and significant issues. These should be your top priority.
                </p>
              </div>
            </div>

            <div className="flex items-center p-3 rounded-lg bg-orange-50">
              <span className="text-2xl mr-3">🌡️</span>
              <div>
                <h3 className="font-medium text-orange-900">Hot (60-79)</h3>
                <p className="text-sm text-orange-700">
                  Important flows with notable usage and errors. Address these after critical issues.
                </p>
              </div>
            </div>

            <div className="flex items-center p-3 rounded-lg bg-yellow-50">
              <span className="text-2xl mr-3">☀️</span>
              <div>
                <h3 className="font-medium text-yellow-900">Warm (40-59)</h3>
                <p className="text-sm text-yellow-700">
                  Moderate usage flows with some issues. Monitor and fix when time permits.
                </p>
              </div>
            </div>

            <div className="flex items-center p-3 rounded-lg bg-blue-50">
              <span className="text-2xl mr-3">🌤️</span>
              <div>
                <h3 className="font-medium text-blue-900">Cool (20-39)</h3>
                <p className="text-sm text-blue-700">
                  Low usage or minor issues. These are generally stable.
                </p>
              </div>
            </div>

            <div className="flex items-center p-3 rounded-lg bg-gray-50">
              <span className="text-2xl mr-3">❄️</span>
              <div>
                <h3 className="font-medium text-gray-900">Cold (0-19)</h3>
                <p className="text-sm text-gray-700">
                  Minimal usage with no significant issues. Low priority for optimization.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Affects Heat Scores */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            What Affects Heat Scores?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                Error Impact (40% weight)
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Error rate percentage</li>
                <li>• Number of users affected</li>
                <li>• Frequency of errors</li>
                <li>• Revenue impact multiplier</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                <UserGroupIcon className="h-5 w-5 text-blue-500 mr-2" />
                Usage Patterns (20% weight)
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Total executions</li>
                <li>• Unique users</li>
                <li>• Daily run average</li>
                <li>• Usage trends</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                <ClockIcon className="h-5 w-5 text-green-500 mr-2" />
                Recency Factor
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Last execution time</li>
                <li>• Recent error occurrences</li>
                <li>• Activity in past week</li>
                <li>• Stale flow detection</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                <ChartBarIcon className="h-5 w-5 text-purple-500 mr-2" />
                Business Impact
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Revenue-touching (1.5x)</li>
                <li>• Customer-facing (1.3x)</li>
                <li>• Integration flows (1.2x)</li>
                <li>• Performance grade penalty</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <LightBulbIcon className="h-6 w-6 text-yellow-500 mr-2" />
            Best Practices
          </h2>
          
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Run heat analysis weekly to track changes in flow usage patterns</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Calculate performance scores first for more accurate heat analysis</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Focus on flows with heat scores above 60 for immediate impact</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Review auto-detected business categories and adjust if needed</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Use heat scores to prioritize your flow optimization efforts</span>
            </li>
          </ul>
        </section>

        {/* Requirements */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-6 w-6 text-gray-500 mr-2" />
            Requirements
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Salesforce Requirements</h3>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>API version 47.0 or higher</li>
                <li>Access to FlowInterview object</li>
                <li>View All Data permission or appropriate object access</li>
                <li>Flow execution history retention enabled</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-2">Data Requirements</h3>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Flows must have been executed in the analysis timeframe</li>
                <li>FlowInterview records must be retained (check Setup → Data Management)</li>
                <li>At least one flow execution in the last 30 days</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Privacy Note */}
        <section className="bg-blue-50 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
            <InformationCircleIcon className="h-6 w-6 text-blue-700 mr-2" />
            Privacy & Security
          </h2>
          <p className="text-blue-800">
            Heat analysis only retrieves aggregated statistics from Salesforce. No individual 
            execution records, user data, or error details are stored in FlowDocs. We only 
            keep summary counts and calculated scores to protect your data privacy.
          </p>
        </section>

        {/* Troubleshooting */}
        <section className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Common Issues
          </h2>
          
          <div className="space-y-4">
            <details className="group">
              <summary className="cursor-pointer font-medium text-gray-900 hover:text-blue-600">
                "No flow interview data available" message
              </summary>
              <div className="mt-2 pl-4 text-gray-600">
                <p>This usually means:</p>
                <ul className="mt-1 list-disc list-inside text-sm">
                  <li>No flows have been executed in the last 30 days</li>
                  <li>FlowInterview records are being purged too quickly</li>
                  <li>Your user doesn't have access to FlowInterview object</li>
                </ul>
              </div>
            </details>

            <details className="group">
              <summary className="cursor-pointer font-medium text-gray-900 hover:text-blue-600">
                "FlowInterview object is not accessible" error
              </summary>
              <div className="mt-2 pl-4 text-gray-600">
                <p>Check these settings in Salesforce:</p>
                <ul className="mt-1 list-disc list-inside text-sm">
                  <li>Ensure your API version is 47.0 or higher</li>
                  <li>Verify user profile has API Enabled permission</li>
                  <li>Check object-level security for FlowInterview</li>
                  <li>Some Salesforce editions may not support this object</li>
                </ul>
              </div>
            </details>

            <details className="group">
              <summary className="cursor-pointer font-medium text-gray-900 hover:text-blue-600">
                Heat scores seem incorrect
              </summary>
              <div className="mt-2 pl-4 text-gray-600">
                <p>Heat scores depend on multiple factors:</p>
                <ul className="mt-1 list-disc list-inside text-sm">
                  <li>Run performance scoring first for accurate grades</li>
                  <li>Check if flows are correctly categorized (revenue/customer)</li>
                  <li>Verify the analysis timeframe matches your expectations</li>
                  <li>Recent flows get higher scores due to recency boost</li>
                </ul>
              </div>
            </details>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500">
          <p>
            Need more help? <Link to="/help" className="text-blue-600 hover:text-blue-800">Back to Help Center</Link>
          </p>
        </div>
      </div>
    </div>
  );
}